import type { ReactNode } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";

// THE text-action for card/row action strips — the house idiom
// (Vedi ▾ · Segui · Modifica › · Elimina) that the Programs/Plans rows
// hand-rolled per-file via programsPlanStyles (rowAction + changeLinkText
// + deleteLinkText). One place for the 44pt touch floor, the pressed /
// disabled dim, the ink3-when-disabled recolor and the destructive
// variant, so the idiom can't drift again (§CONSOLIDAMENTO, HR-S4).
// State is never colour-alone: disabled also lands in a11y state and,
// by house convention, in the label itself (e.g. "Following…").
export function RowAction({
  theme,
  label,
  onPress,
  disabled = false,
  destructive = false,
  accessibilityLabel,
  accessibilityHint,
  expanded,
  trailing,
}: {
  theme: UiTheme;
  label: string;
  onPress: () => void;
  disabled?: boolean;
  /** Danger-coloured label (delete). */
  destructive?: boolean;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  /** Set on expand/collapse toggles (lands in a11y state). */
  expanded?: boolean;
  /** Trailing adornment (e.g. the ▾ chevron of a preview toggle). */
  trailing?: ReactNode;
}) {
  const styles = makeStyles(theme);
  const text = (
    <Text
      style={[
        destructive ? styles.destructiveText : styles.text,
        disabled && !destructive && styles.disabledText,
      ]}
    >
      {label}
    </Text>
  );
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      accessibilityState={{
        disabled: disabled || undefined,
        expanded,
      }}
      disabled={disabled}
      onPress={onPress}
      hitSlop={8}
      style={({ pressed }) => [
        styles.action,
        (pressed || disabled) && styles.pressed,
      ]}
    >
      {trailing ? (
        <View style={styles.iconRow}>
          {text}
          {trailing}
        </View>
      ) : (
        text
      )}
    </Pressable>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    // 44pt floor via minHeight (the strip is dense, hitSlop tops it up).
    action: {
      minHeight: 44,
      paddingVertical: s.s2,
      justifyContent: "center",
    },
    pressed: { opacity: 0.6 },
    text: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "600",
    },
    disabledText: {
      color: p.ink3,
    },
    destructiveText: {
      color: p.danger,
      fontSize: t.sizes.sm,
      fontWeight: "700",
    },
    iconRow: { flexDirection: "row", alignItems: "center", gap: s.s2 },
  });
}
